import { useAuth } from './useAuth'
import { useChangeMetadataMutation } from '@gql'
import { UserMetadata } from '@rolebase/shared/model/user'
import { useCallback, useEffect, useRef, useState } from 'react'
import { nhost } from 'src/nhost'

// Return current user metadata, a function to set one metadata by its key, and
// one to set several at once.
export default function useUserMetadata() {
  const [updateMetadata] = useChangeMetadataMutation()

  // Get user metadata
  const { user } = useAuth()
  const userId = user?.id
  const metadata: UserMetadata | undefined = user?.metadata

  // Cache metadata for optimistic UI
  const [cachedMetadata, setCachedMetadata] = useState(metadata)

  // The same value, kept in a ref so a second write in the SAME handler merges
  // on the first one's result instead of on the render's copy.
  //
  // 🔴 Without it, `await setMetadata('a', 1)` then `await setMetadata('b', 2)`
  // both start from the metadata captured at render — React has not
  // re-rendered between the two awaits — so the second write erases the first.
  // A screen saving several preferences at once would keep only the last.
  const latestMetadata = useRef(metadata)

  useEffect(() => {
    if (!metadata) return
    setCachedMetadata(metadata)
    latestMetadata.current = metadata
  }, [metadata])

  // Set several preferences in one go.
  //
  // ⚠️ `changeMetadata` REPLACES the whole jsonb column, so the merge below is
  // what protects everything the caller did not mention — the timezone, the
  // digest rule, the onboarding answers.
  const setMetadataMany = useCallback(
    async (changes: Partial<UserMetadata>) => {
      if (!userId) return

      const newMetadata = { ...latestMetadata.current, ...changes }

      // Update local state, ref first so a follow-up call in the same handler
      // sees this write.
      latestMetadata.current = newMetadata
      setCachedMetadata(newMetadata)

      // Update remote state, once for every key
      await updateMetadata({
        variables: {
          userId,
          metadata: newMetadata,
        },
      })

      // Refresh user data
      await nhost.refreshSession(0)
    },
    [userId, updateMetadata]
  )

  // Set preference value
  const setMetadata = useCallback(
    async <Key extends keyof UserMetadata>(
      key: Key,
      value: UserMetadata[Key]
    ) => setMetadataMany({ [key]: value } as Partial<UserMetadata>),
    [setMetadataMany]
  )

  return {
    metadata: cachedMetadata,
    setMetadata,
    setMetadataMany,
  }
}
